import { useMsal } from "@azure/msal-react";
import {
    Button,
    Flex,
    Group,
    Modal,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFilterFnsState,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { useMemo, useRef, useState } from "react";
import { fetchWithAuth, getAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";
import { RoleSelectionComponent, type User, useUserOperations } from "./manageUsers";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export type Request = {
    id: string;
    user_id: string;
    user_email: string;
    request_datetime: number;
    requested_datasources: { id: string }[];
    status: string;
    reason: string;
};

interface QueryParams {
    columnFilterFns: MRT_ColumnFilterFnsState;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    sorting: MRT_SortingState;
    pagination: MRT_PaginationState;
}

interface UpdateRequestStatusParams {
    user_id: string;
    updated_status: string;
    rejection_reason: string | null;
}

interface Role {
    id: string;
    description: string;
    datasources: { id: string }[];
}

// Create a custom hook for request operations
const useRequestOperations = (params: QueryParams) => {
    const isAdmin = useAdmin();
    const { instance } = useMsal();
    const { columnFilterFns, columnFilters, globalFilter, sorting, pagination } = params;

    // GET operation - call hook at top level
    const fetchURL = new URL("/v1/auth/access-requests", BACKEND_URL);
    fetchURL.searchParams.set("start", `${pagination.pageIndex * pagination.pageSize}`);
    fetchURL.searchParams.set("size", `${pagination.pageSize}`);
    fetchURL.searchParams.set("filters", JSON.stringify(columnFilters ?? []));
    fetchURL.searchParams.set("filterModes", JSON.stringify(columnFilterFns ?? {}));
    fetchURL.searchParams.set("globalFilter", globalFilter ?? "");
    fetchURL.searchParams.set("sorting", JSON.stringify(sorting ?? []));

    const requestsQuery = useInfiniteQuery<Request[]>({
        queryKey: ["access-requests", pagination],
        queryFn: async () => {
            // Note: enabled: isAdmin already prevents this query from running for non-admins
            const response = await getAPI({ url: fetchURL.href, instance });
            const data = await response?.json();
            return data;
        },
        initialPageParam: 0,
        getNextPageParam: () => undefined,
        placeholderData: (previousData) => previousData,
        enabled: isAdmin,
    });

    // PATCH operation
    const updateRequest = async ({
        user_id,
        updated_status,
        rejection_reason,
    }: UpdateRequestStatusParams) => {
        const response = await fetchWithAuth({
            url: `${BACKEND_URL}/v1/auth/access-requests`,
            method: "PATCH",
            payload: { user_id, updated_status, rejection_reason },
            instance,
        });

        if (!response?.ok) {
            throw new Error("Failed to update request status");
        }

        return response;
    };

    return { requestsQuery, updateRequest };
};

const useRoles = () => {
    const { instance } = useMsal();

    return useQuery<Role[], Error>({
        queryKey: ["roles"],
        queryFn: async () => {
            const response = await getAPI({
                url: `${BACKEND_URL}/v1/auth/roles`,
                instance,
            });
            const jsonData = await response?.json();
            return jsonData;
        },
    });
};

const ManageRequests = () => {
    const { data: roles, isLoading: isLoadingRoles, refetch: fetchRoles } = useRoles();
    // Use stable empty array reference to prevent unnecessary re-renders
    const stableRoles = useMemo(() => roles ?? [], [roles]);

    const columns = useMemo<MRT_ColumnDef<Request>[]>(
        () => [
            {
                accessorKey: "user_id" as const,
                header: "User ID",
                accessorFn: (row) => row?.user_id ?? "",
            },
            {
                accessorKey: "user_email" as const,
                header: "User Email",
                accessorFn: (row) => row?.user_email ?? "",
            },
            {
                accessorKey: "request_datetime" as const,
                header: "Requested at",
                accessorFn: (row) =>
                    row ? new Date(1000 * row.request_datetime).toLocaleString() : "",
            },
            {
                accessorKey: "requested_datasources" as const,
                header: "Requested DataSources",
                accessorFn: (row) =>
                    row?.requested_datasources?.map((ds) => ds.id).join(", ") ?? "",
            },
            {
                accessorKey: "status" as const,
                header: "Status",
                accessorFn: (row) => row?.status ?? "",
            },
            {
                accessorKey: "reason" as const,
                header: "Reason",
                accessorFn: (row) => row?.reason ?? "",
            },
        ],
        [],
    );

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [columnFilterFns] = useState<MRT_ColumnFilterFnsState>(
        Object.fromEntries(columns.map(({ accessorKey }) => [accessorKey, "contains"])),
    ); //default to "contains" for all columns
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const {
        usersQuery: { data: users, isLoading: isLoadingUsers },
        updateUser,
    } = useUserOperations({
        columnFilterFns,
        columnFilters,
        globalFilter,
        pagination,
        sorting,
    });

    const fetchedUsers = useMemo(() => {
        if (!users) return [];
        return (
            users.pages?.flatMap((page: unknown) =>
                Array.isArray(page) ? (page as User[]) : [],
            ) ?? []
        );
    }, [users]);

    const {
        requestsQuery: { data: requests, isLoading: isLoadingRequests, refetch },
        updateRequest,
    } = useRequestOperations({
        columnFilterFns,
        columnFilters,
        globalFilter,
        pagination,
        sorting,
    });

    const fetchedRequests = useMemo(() => {
        if (!requests) return [];
        return (
            requests.pages?.flatMap((page: unknown) =>
                Array.isArray(page) ? (page as Request[]) : [],
            ) ?? []
        );
    }, [requests]);

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [isRefetching, setIsRefetching] = useState(false);

    const fetchMoreOnBottomReached = async (containerRefElement?: HTMLDivElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            const lastPage = requests?.pages?.[requests.pages.length - 1];
            if (
                scrollHeight - scrollTop - clientHeight < 400 &&
                !isRefetching &&
                lastPage &&
                Array.isArray(lastPage) &&
                lastPage.length === pagination.pageSize
            ) {
                setIsRefetching(true);
                await setPagination((old) => ({
                    ...old,
                    pageIndex: old.pageIndex + 1,
                }));
                setIsRefetching(false);
            }
        }
    };

    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);

    const [modalOpened, setModalOpened] = useState(false);
    const [isSubmittingApprove, setIsSubmittingApprove] = useState(false);
    const [isSubmittingReject, setIsSubmittingReject] = useState(false);

    const [roleSearchTerm, setRoleSearchTerm] = useState("");

    const [rejectRequestModalOpened, { open: openRejectRequest, close: closeRejectRequest }] =
        useDisclosure(false);
    const [rejectionReason, setRejectionReason] = useState<string>("");

    const form = useForm({
        initialValues: {
            user_id: "",
            user_email: "",
            request_datetime: 0,
            requested_datasources: [] as { id: string }[],
            status: "",
            reason: "",
            roles: [] as string[],
        },
        validate: {
            requested_datasources: (value) =>
                value.length === 0 ? "At least one data source is required" : null,
            roles: (value) => (value.length === 0 ? "At least one role is required" : null),
        },
    });

    const handleOpenModal = (request: Request) => {
        if (isLoadingRoles) {
            notifications.show({
                title: "Loading",
                message: "Please wait while roles are being loaded...",
                loading: true,
            });
            return;
        }

        if (stableRoles.length === 0) {
            fetchRoles();
        }

        const user = fetchedUsers.find((u: User) => u.id === request.user_id);
        const currentUser = fetchedUsers.find((u: User) => u.id === user.id) || user;

        setSelectedRequest(request);
        setSelectedUser(currentUser);

        form.setValues({
            user_id: currentUser.id,
            user_email: currentUser.email,
            request_datetime: request.request_datetime,
            requested_datasources: request.requested_datasources,
            status: request.status,
            reason: request.reason,
            roles: currentUser.roles,
        });
        setModalOpened(true);
    };

    const handleApprove = async (user_id: string, roleKeys: string[]) => {
        setIsSubmittingApprove(true);
        try {
            if (!selectedUser) {
                throw new Error("No user selected");
            }

            const updatedUser = await updateUser({
                data: {
                    id: selectedUser.id,
                    email: selectedUser.email,
                    org_code: selectedUser.org_code,
                    roles: roleKeys,
                },
            });

            setSelectedUser(updatedUser);

            const userIndex = fetchedUsers.findIndex((u: User) => u.id === updatedUser.id);
            if (userIndex !== -1) {
                const updatedUsers = [...fetchedUsers];
                updatedUsers[userIndex] = updatedUser;
            }
            await updateRequest({
                user_id: user_id,
                updated_status: "approved",
                rejection_reason: null,
            });
            notifications.show({
                title: "Success",
                message: "Request approved successfully",
                color: "green",
            });
            refetch();
            setModalOpened(false);
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to approve the request",
                color: "red",
            });
        } finally {
            setIsSubmittingApprove(false);
        }
    };

    const handleReject = async (user_id: string) => {
        setIsSubmittingReject(true);
        closeRejectRequest();
        try {
            if (!selectedUser) {
                throw new Error("No user selected");
            }
            await updateRequest({
                user_id: user_id,
                updated_status: "rejected",
                rejection_reason: rejectionReason,
            });
            notifications.show({
                title: "Success",
                message: "Request rejected successfully",
                color: "green",
            });
            refetch();
            setModalOpened(false);
            setRejectionReason("");
            closeRejectRequest();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to reject the request",
                color: "red",
            });
        } finally {
            setIsSubmittingReject(false);
        }
    };

    const table = useMantineReactTable<Request>({
        layoutMode: "grid",
        columns,
        data: fetchedRequests ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${fetchedRequests.length}, (Showing ${table.getFilteredRowModel().rows.length})`}
                </Text>
            </Flex>
        ),
        enableColumnResizing: true,
        enableFilters: true,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row: Request) => row.id,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableDensityToggle: false,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: "calc(100vh - 280px)" },
            onScroll: (event) => fetchMoreOnBottomReached(event.currentTarget),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleOpenModal(row.original),
            sx: { cursor: "pointer" },
        }),
        state: {
            isLoading: isLoadingRequests || isLoadingRoles || isLoadingUsers,
            showProgressBars: isRefetching,
            columnFilters,
            globalFilter,
            sorting,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
    });
    return (
        <>
            <Flex px="xl" pb="xl" direction="column" gap="md">
                <Flex gap="md" align="center">
                    <Title lh={2} c="purple.8" order={2} style={{ textTransform: "uppercase" }}>
                        Manage Requests
                    </Title>
                </Flex>
            </Flex>

            <MantineReactTable table={table} />
            <Modal
                opened={modalOpened}
                onClose={() => {
                    setModalOpened(false);
                    form.reset();
                    setRoleSearchTerm("");
                }}
                title={`Handle User request: ${selectedUser?.email || ""}`}
                size="xl"
                styles={{
                    body: {
                        minHeight: 500,
                    },
                }}
            >
                <form>
                    <Stack gap="md">
                        <SimpleGrid cols={2}>
                            <Stack>
                                <TextInput
                                    label="Email"
                                    value={selectedUser?.email || ""}
                                    readOnly
                                />
                                <SimpleGrid cols={2}>
                                    <TextInput
                                        label="User ID"
                                        value={selectedUser?.id || ""}
                                        readOnly
                                    />
                                    <TextInput
                                        label="Org Code"
                                        value={selectedUser?.org_code || ""}
                                        readOnly
                                    />
                                </SimpleGrid>
                                <SimpleGrid cols={2}>
                                    <TextInput
                                        label="Requested At"
                                        value={
                                            new Date(
                                                1000 * (selectedRequest?.request_datetime || 0),
                                            )?.toLocaleString() || ""
                                        }
                                        readOnly
                                    />
                                    <TextInput
                                        label="Status"
                                        value={selectedRequest?.status || ""}
                                        readOnly
                                    />
                                </SimpleGrid>
                                <Textarea
                                    label="Reason"
                                    value={selectedRequest?.reason || ""}
                                    readOnly
                                    autosize
                                    minRows={2}
                                />
                                <Textarea
                                    label="Requested Datasources"
                                    value={
                                        selectedRequest?.requested_datasources
                                            ?.map((ds) => ds.id)
                                            .join(", ") ?? ""
                                    }
                                    readOnly
                                    autosize
                                    minRows={2}
                                />
                            </Stack>
                            <RoleSelectionComponent
                                form={form}
                                roleSearchTerm={roleSearchTerm}
                                setRoleSearchTerm={setRoleSearchTerm}
                            />
                        </SimpleGrid>

                        <Group justify="flex-end" mt="md">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setModalOpened(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="green"
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    handleApprove(selectedRequest?.user_id || "", form.values.roles)
                                }
                                loading={isSubmittingApprove}
                            >
                                Approve
                            </Button>
                            <Button
                                color="red"
                                type="button"
                                variant="outline"
                                onClick={openRejectRequest}
                                loading={isSubmittingReject}
                            >
                                Reject
                            </Button>
                            <Modal
                                size="lg"
                                opened={rejectRequestModalOpened}
                                onClose={closeRejectRequest}
                                title="Provide a rejection reason"
                            >
                                <Stack align="flex-end">
                                    <Textarea
                                        placeholder="Please as an admin explain why you are rejecting this request"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        style={{ width: "100%" }}
                                        minRows={4}
                                        required
                                    />
                                    <Button
                                        onClick={() => handleReject(selectedRequest?.user_id || "")}
                                        disabled={!rejectionReason.trim()}
                                        color="red"
                                    >
                                        Reject
                                    </Button>
                                </Stack>
                            </Modal>
                        </Group>
                    </Stack>
                </form>
            </Modal>
        </>
    );
};

export default ManageRequests;
